import {useCallback, useState} from 'react'
import {markLocationWorking} from '@/utils/locationSignal'
import {useLocationAccess} from '@/hooks/useLocationAccess'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import FromAirportArrived from '@/layouts/FromAirportArrived'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import {isFlightArrived} from '@/features/fromAirportArrival/flightStatus'

/**
 * "WELCOME! YOU'VE ARRIVED." — as a modal, over whatever page the passenger is on.
 *
 * ── ⚠️ WHY IT STOPPED BEING A PAGE (2026-09-22) ───────────────────────────────────
 * The arrival guide lived at `/:tripId/welcome`, and a landed flight NAVIGATED there. That
 * had two costs and the second one was the expensive one:
 *
 *   · it took the passenger off whatever they were doing, which on a polled condition can
 *     happen at any moment;
 *   · two pages then had to agree about who owned a landed, pre-boarding airport ride.
 *     /track-driver handed those back to /welcome, /welcome's "I've arrived" navigated to
 *     /track-driver, and the pair bounced — so `?arrived=1` was invented purely to break
 *     the loop. A flag that exists to stop two screens fighting is a sign the screens were
 *     the wrong shape.
 *
 * A modal owns nothing. It opens over the page the passenger chose, closes back to it, and
 * has nowhere to bounce to — so the loop and its flag both deleted themselves.
 *
 * ⚠️ AND THE ROUTE IS GONE ENTIRELY (2026-09-23), not merely unlinked. It was kept for a
 * day as a deep link, which left the welcome reachable as a PAGE — so there were still two
 * screens that could own a landed airport ride, and the second cost above was still being
 * paid. `ArrivalGuideRoute`, the gate that gave that URL its answers, went with it. This
 * modal and the plane button in `AppLayout` are the only way to the guide now.
 *
 * ── ⚠️ THE LOCATION ASK LIVES HERE, AND IT IS A BUTTON ────────────────────────────
 * The welcome screen used to open a `watchPosition` on mount, which raised the native
 * prompt unattended over a screen nobody had touched. That is the prompt most likely to be
 * dismissed, and on Chrome repeated dismissals turn into a permanent block that no code can
 * prompt out of afterwards. iOS Safari often wants a gesture for it at all.
 *
 * So the ask is a card with a button, shown only while the browser says the question is
 * still open. Granted, it never appears. Blocked, it says where the setting is instead of
 * offering a button that cannot work — and `PermissionStatus.onchange` clears it by itself
 * the moment they fix it.
 *
 * ⚠️ AND IT IS NEVER A BLOCK. Everything below it renders regardless: the walk, the
 * driver, the car, all of it. A passenger is a customer standing in a terminal, not a
 * device to be negotiated with — see the note where the old `LocationRequired` gate was
 * removed from `FromAirportArrived`.
 */
export default function WelcomeModal({trip, onClose}) {
    /* 'unknown' until the browser answers — the card stays hidden rather than flashing at
       a passenger who granted this weeks ago. */
    // The ONE shared answer (hooks/useLocationAccess) — same as every other screen.
    // 'checking' reads as unknown: the card stays hidden rather than flashing.
    const {state: shared, request} = useLocationAccess()
    const permission = shared === 'checking' ? 'unknown' : shared
    const [asking, setAsking] = useState(false)

    /* The button raises the browser's prompt through the shared hook, so the answer lands
       in the one shared state and every screen sees it. */
    const ask = useCallback(async () => {
        setAsking(true)
        try {
            if (await request()) markLocationWorking()
        } finally {
            setAsking(false)
        }
    }, [request])

    const blocked = permission === 'denied'
    const showAsk = permission === 'prompt' || blocked

    /**
     * WHAT THE HEADER SAYS — the same three states the body renders, asked the same way.
     *
     * ⚠️ IT WAS "WELCOME! YOU'VE ARRIVED." UNCONDITIONALLY, over a body reading "Flight is
     * on the way". The modal greeted a passenger into an arrivals hall while its own card
     * showed their aircraft four hours out — and the two headings sat two lines apart, so it
     * was not a subtlety anyone could miss.
     *
     * ⚠️ THE SAME HELPERS THE BODY USES, NOT A SECOND RULE. `tripFlightRecord` picks the
     * right LEG of `provider_data` and `isFlightArrived` owns the word that means down; a
     * header testing `flight_status` for itself is how two screens start disagreeing about
     * one aircraft.
     *
     * ⚠️ AND AN UNPOLLED FLIGHT KEEPS THE GREETING, exactly as `FromAirportArrived`
     * decides it. `flight` is null while `provider_data` is unfilled, and the modal is
     * raised BY a landing — so "no record yet" must not read as "still flying".
     */
    const flightRecord = tripFlightRecord(trip)
    const trackingFailed = trip?.flight?.tracking_status === 'failed'
    const airborne = !!flightRecord && !isFlightArrived(flightRecord)
    const title = trackingFailed
        ? "We couldn't track this flight"
        : airborne
            ? 'Flight is on the way'
            : "Welcome! You've arrived"

    /* ⚠️ WIDER THAN THE STANDARD PANEL, AND ONLY THIS ONE. `Modal` caps at max-w-200
       (800px), which suits a single column of form; this screen is TWO — the steps and the
       walk on the left, the driver, the plate and the trip on the right — and at 800px each
       of them is a narrow strip with its headings wrapping onto three lines. The cap is
       raised here through `className` rather than in `Modal`, so every other modal in the
       portal keeps the width it was designed at. The `w-` pair holds a 2rem gutter either
       side, so the panel never reaches the edge of a 1120px screen. */
    return (
        <Modal isOpen={!!trip} onClose={onClose} className="md:w-[calc(100%-4rem)] md:max-w-280">
            <ModalHeader title={title} onClose={onClose}/>

            {showAsk && (
                <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-(--border-color) p-4 max-md:mb-4 max-md:p-3">
                    <div className="flex flex-col gap-1">
                        <p className="font-semibold text-(--text-primary)">
                            {blocked ? 'Location is blocked' : 'Share your location'}
                        </p>
                        <p className="text-sm text-(--text-secondary) max-md:text-xs">
                            {blocked
                                ? 'Open your browser’s site settings for this page and allow location. Your driver can then see where to meet you.'
                                : 'So your driver knows where to meet you, and we can guide you to the pickup point. You can skip this — everything below still works.'}
                        </p>
                    </div>

                    {!blocked && (
                        <button
                            type="button"
                            onClick={ask}
                            disabled={asking}
                            className="cursor-pointer rounded-full bg-(--text-primary) p-3 text-center text-white hover:opacity-80 disabled:cursor-wait disabled:opacity-60"
                        >
                            {asking ? 'Waiting for your browser…' : 'Allow location'}
                        </button>
                    )}
                </div>
            )}

            {/* ⚠️ `embedded` — the greeting block inside is dropped, because the header
                above already says the same words. Everything else is the screen exactly as
                the route renders it, including its own walk and chat sheets. */}
            <FromAirportArrived tripId={trip?.id} embedded onLeave={onClose}/>
        </Modal>
    )
}
